import React from 'react';
import { useNavigate } from 'react-router-dom'; // <-- 1. IMPORTADO O HOOK
import { 
  Dices, 
  Coffee, 
  Users, 
  Sparkles, 
  ArrowRight, 
  Sword
} from 'lucide-react';

import deliFrente from '../../assets/images/deli-frente.png';
import deliDentro from '../../assets/images/deli-dentro.png';
import deliAcervo from '../../assets/images/deli-jogos.png';

// --- Shared UI Components ---

const SectionSeparator = () => (
  <div className="flex justify-center items-center py-16 opacity-40">
    <div className="h-[1px] w-16 bg-gradient-to-r from-transparent to-[#971253]"></div>
    <div className="mx-4 text-[#971253]">
      <Sparkles size={16} strokeWidth={1} />
    </div>
    <div className="h-[1px] w-16 bg-gradient-to-l from-transparent to-[#971253]"></div>
  </div>
);

// <-- 2. ADICIONADO A PROP onClick AQUI
const BentoCard = ({ icon: Icon, title, subtitle, color, className, onClick }) => (
  <div 
    onClick={onClick} // <-- APLICADO O EVENTO DE CLIQUE
    className={`
    group relative overflow-hidden p-8 
    bg-white/60 backdrop-blur-sm border border-white/50
    rounded-2xl shadow-sm hover:shadow-lg hover:-translate-y-1 
    transition-all duration-500 ease-out cursor-pointer
    ${className}
  `}>
    <div className={`
      absolute top-0 right-0 w-24 h-24 -mr-8 -mt-8 
      rounded-full opacity-10 group-hover:scale-150 transition-transform duration-700
      ${color === 'teal' ? 'bg-[#528a89]' : 'bg-[#971253]'}
    `}/>
    
    <Icon className={`w-8 h-8 mb-4 ${color === 'teal' ? 'text-[#528a89]' : 'text-[#971253]'}`} strokeWidth={1.5} />
    
    <h3 className="font-serif text-2xl text-[#971253] mb-2">{title}</h3>
    <p className="text-stone-600 text-sm leading-relaxed">{subtitle}</p>
    
    <div className="mt-6 flex items-center text-xs font-medium uppercase tracking-widest text-stone-400 group-hover:text-[#971253] transition-colors">
      Explorar <ArrowRight size={14} className="ml-2 group-hover:translate-x-1 transition-transform" />
    </div>
  </div>
);

// --- Main Page Component ---

const DeliDaPersyAbout = () => {
  const navigate = useNavigate(); // <-- 3. INICIALIZADO O HOOK


  return (
    <div className="min-h-screen bg-[#FFF5EE] text-stone-800 font-sans selection:bg-[#971253] selection:text-white overflow-x-hidden">

      {/* 1. HERO SECTION */}
      <section className="relative min-h-[90vh] pt-32 pb-40 flex flex-col items-center justify-center px-6 text-center bg-[#528a89] overflow-hidden">
        
        {/* Decorative Background Elements */}
        <div className="absolute top-0 left-0 w-64 h-64 border-r border-b border-[#FFF5EE]/20 rounded-br-[100px] pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-64 h-64 border-l border-t border-[#FFF5EE]/20 rounded-tl-[100px] pointer-events-none" />
        
        {/* Animated Content */}
        <div className="max-w-4xl z-10 animate-in fade-in slide-in-from-bottom-8 duration-1000">
          
          <span className="block text-[#FFF5EE] uppercase tracking-[0.2em] text-sm font-medium mb-8">
            Juazeiro do Norte
          </span>
          
          {/* HEADLINE: Seashell Text + Dark Teal Shadow */}
          <h1 className="font-serif text-6xl md:text-8xl text-[#FFF5EE] leading-[1.1] mb-10 [text-shadow:4px_4px_0px_#234e4d]">
            Onde Histórias são Jogadas <br/> 
            <span className="italic text-5xl md:text-7xl opacity-90 block mt-2">
              e Memórias Compartilhadas
            </span>
          </h1>
          
          <p className="max-w-lg mx-auto text-lg text-[#FFF5EE] leading-relaxed mb-12 font-light drop-shadow-md">
            Um santuário para jogos de tabuleiro, gastronomia e a magia da conexão presencial. 
            Desconecte-se das telas e role os dados.
          </p>

          <button 
            onClick={() => navigate('/sobre/reserva')}// <-- APLICADO ROTA AQUI
            className="group relative px-10 py-5 bg-[#971253] text-[#FFF5EE] rounded-full overflow-hidden shadow-[0_10px_20px_-5px_rgba(0,0,0,0.3)] hover:shadow-xl hover:shadow-[#971253]/40 transition-all border border-[#FFF5EE]/30"
          >
            <span className="relative z-10 flex items-center font-medium tracking-wide text-lg">
              Reservar uma Mesa <Dices className="ml-2 w-5 h-5 group-hover:rotate-180 transition-transform duration-500" />
            </span>
            <div className="absolute inset-0 bg-[#7a0e42] transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300 ease-out" />
          </button>
        </div>

        {/* Bottom Wave to transition smoothly to the white section */}
        <div className="absolute bottom-0 left-0 right-0">
           <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320" className="w-full h-auto block align-middle">
              <path fill="#FFF5EE" fillOpacity="1" d="M0,224L48,213.3C96,203,192,181,288,181.3C384,181,480,203,576,224C672,245,768,267,864,250.7C960,235,1056,181,1152,165.3C1248,149,1344,171,1392,181.3L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
           </svg>
        </div>
      </section>

      <SectionSeparator />

      {/* 2. OUR STORY (Editorial Layout) */}
      <section className="py-24 px-6 md:px-12 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          
          {/* Image Area */}
          <div className="md:col-span-5 relative">
            <div className="aspect-[3/4] rounded-t-[100px] rounded-b-2xl overflow-hidden shadow-xl bg-stone-200 relative">
               <img 
                 src={deliFrente}
                 alt="Interior do Deli da Persy" 
                 className="object-cover w-full h-full hover:scale-105 transition-transform duration-1000"
               />
               <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-[#971253]/40 to-transparent mix-blend-multiply" />
            </div>
            {/* Decorative Outline */}
            <div className="absolute -z-10 top-4 -left-4 w-full h-full border border-[#528a89] rounded-t-[100px] rounded-b-2xl" />
          </div>

          {/* Text Content */}
          <div className="md:col-span-7 md:pl-12 space-y-8">
            <h2 className="font-serif text-4xl md:text-5xl text-[#971253]">A Arte de Jogar</h2>
            <div className="prose prose-lg text-stone-600">
              <p className="first-letter:text-5xl first-letter:font-serif first-letter:text-[#528a89] first-letter:float-left first-letter:mr-3">
                Tudo começou com uma observação simples: em um mundo digital, ansiamos por conexões analógicas. 
                O Deli da Persy nasceu da paixão pelo prazer tátil dos jogos de tabuleiro e pela 
                narrativa imersiva dos RPGs.
              </p>
              <p>
                Não somos apenas um café com uma prateleira de Banco Imobiliário. Somos uma experiência curada onde 
                a gastronomia encontra a jogatina. Cada prato é projetado para ser saboreado com uma mão enquanto 
                você rola um d20 com a outra.
              </p>
              <p className="text-[#528a89] italic font-medium">
                "Construímos o lugar onde sempre quisemos estar."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 3. EXPERIENCE (Bento Grid) */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl text-[#971253] mb-4">Escolha Sua Aventura</h2>
            <p className="text-stone-500">Mais do que apenas café. É uma campanha.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 h-auto md:h-[500px]">
            {/* Large Card Left */}
            <div className="md:col-span-1 md:row-span-2 h-full">
               <BentoCard 
                  onClick={() => navigate('/biblioteca')} // <-- ROTA DE CATÁLOGO
                  icon={Dices}
                  title="A Biblioteca"
                  subtitle="Mais de 100 títulos, variando de party games casuais a eurogames de estratégia pesada, com curadoria de nossos Game Masters."
                  color="wine"
                  className="h-full bg-[#FFF5EE]"
               />
            </div>

            {/* Top Right */}
            <div className="md:col-span-2">
               <BentoCard 
                  onClick={() => navigate('/cardapio')} // <-- ROTA DE CARDÁPIO
                  icon={Coffee}
                  title="Cardápio"
                  subtitle="Cafés artesanais, poções (drinks) temáticas e finger foods projetados para manter seus componentes de jogo limpos."
                  color="teal"
                  className="h-full"
               />
            </div>

            {/* Bottom Right Split */}
            <div className="md:col-span-1">
               <BentoCard 
                  onClick={() => navigate('/sobre/rpg')} // <-- ROTA DE RESERVA RPG COM PREFILL
                  icon={Sword}
                  title="Noites de RPG"
                  subtitle="Mesas privadas reservadas para campanhas de D&D com todo material preparado para sua imersão."
                  color="wine"
                  className="h-full"
               />
            </div>
             <div className="md:col-span-1">
               <BentoCard 
                  onClick={() => navigate('/sobre/assinatura')} // <-- ROTA DE ASSINATURA
                  icon={Users}
                  title="Assinatura"
                  subtitle="Se torne um assinante e ganhe os beneficios e a facilidade de poder vir sem pagar o passaporte"
                  color="teal"
                  className="h-full"
               />
            </div>
          </div>
        </div>
      </section>

      {/* 4. ATMOSPHERE */}
      <section className="py-24 px-6 bg-[#FFF5EE]">
        <div className="max-w-7xl mx-auto space-y-24">
          
          {/* Atmosphere Row 1 */}
          <div className="flex flex-col md:flex-row items-center gap-12">
            <div className="flex-1 order-2 md:order-1">
              <div className="space-y-4 max-w-sm ml-auto text-right">
                <h3 className="font-serif text-3xl text-[#528a89]">Imersão Aconchegante</h3>
                <p className="text-stone-600">
                  Iluminação quente, tratamento acústico para conversas claras e mesas 
                  dimensionadas especificamente para grandes tabuleiros de jogo.
                </p>
              </div>
            </div>
            <div className="flex-1 order-1 md:order-2">
              <div className="relative aspect-video rounded-tl-[80px] rounded-br-[80px] overflow-hidden shadow-xl">
                 <img src={deliDentro} alt="Iluminação aconchegante" className="object-cover w-full h-full" />
              </div>
            </div>
          </div>

          {/* Atmosphere Row 2 */}
          <div className="flex flex-col md:flex-row items-center gap-12">
            <div className="flex-1">
              <div className="relative aspect-video rounded-tr-[80px] rounded-bl-[80px] overflow-hidden shadow-xl">
                 <img src={deliAcervo} alt="Amigos rindo" className="object-cover w-full h-full" />
              </div>
            </div>
            <div className="flex-1">
              <div className="space-y-4 max-w-sm">
                <h3 className="font-serif text-3xl text-[#528a89]">Detalhes Mágicos</h3>
                <p className="text-stone-600">
                  Do design do menu à decoração, o Art Nouveau encontra a fantasia. 
                  Cada canto é uma oportunidade para fotos.
                </p>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* 6. FINAL CTA */}
      <section className="py-32 px-6 text-center relative overflow-hidden">
        <div className="max-w-2xl mx-auto relative z-10">
          <h2 className="font-serif text-5xl md:text-7xl text-[#971253] mb-8">Pronto para Jogar?</h2>
          <p className="text-stone-600 text-lg mb-10">
            As mesas lotam rápido nos fins de semana. Garanta seu lugar na campanha.
          </p>
          <button 
            onClick={() => navigate('/sobre/reserva')}  // <-- APLICADO ROTA AQUI
            className="px-10 py-5 bg-[#528a89] text-white rounded-2xl text-lg font-medium tracking-wide hover:bg-[#3f6d6c] hover:scale-105 transition-all shadow-lg hover:shadow-[#528a89]/30"
          >
            Fazer Reserva
          </button>
        </div>
        {/* Abstract Background Ring */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] border border-[#971253]/10 rounded-full z-0 pointer-events-none" />
      </section>
      
      {/* Custom Keyframes for Marquee */}
      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          animation: marquee 20s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default DeliDaPersyAbout;